import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import { DeleteIcon } from '@/assets/icons/icons';
import SortableRow from '../../DirectContactList/SortableRow';
import { Flex } from 'antd';
import { SortableActionsProps } from '../type';
import { getActionIcon } from '../utils/getActionIcon';

const SortableActions: React.FC<SortableActionsProps> = ({
  actionsList,
  tpSelected,
  setTpSelected,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const [items, setItems] = useState(actionsList);

  // Sync list with selected ids only
  useEffect(() => {
    const mapped = tpSelected
      .map((action) =>
        actionsList.find((x) => x?.action_type === action?.action_type)
      )
      .filter(Boolean);
    setItems(mapped);
  }, [tpSelected, actionsList]);

  // DELETE item
  const handleDelete = useCallback(
    (action_type: string) => {
      setTpSelected((prev) =>
        prev.filter((x) => x?.action_type !== action_type)
      );
    },
    [setTpSelected]
  );

  // REORDER items
  const handleReorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      const updated = arrayMove(items, oldIndex, newIndex);
      setItems(updated);
      setTpSelected(updated);
    },
    [items, setTpSelected]
  );

  // render list rows
  const list = useMemo(() => {
    return items.map((item) => {
      const Icon = getActionIcon(item.action_type);

      return (
        <div key={item?.action_type} className='aad-list'>
          <div className='aad-item selected-list'>
            <Flex className='w-full' justify='space-between' align='center'>
              <SortableRow
                id={String(item.action_type)}
                key={item.action_type}
                showDrag
                className='dnd-row-class selected-action'
              >
                <div className='icon'>
                  {Icon && <Icon width={16} height={16} />}
                </div>

                <div className='details'>
                  <div className='title'>{item.title}</div>
                  <div className='desc'>{item.description}</div>
                </div>
              </SortableRow>
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => handleDelete(item?.action_type)}
              >
                <DeleteIcon color='var(--text-primary)' />
              </span>
            </Flex>
          </div>
        </div>
      );
    });
  }, [items, handleDelete]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;

        const ids = items.map((i) => String(i.action_type));
        const from = ids.indexOf(String(active.id));
        const to = ids.indexOf(String(over.id));

        if (from !== -1 && to !== -1) handleReorder(from, to);
      }}
    >
      <SortableContext
        items={items.map((i) => String(i.action_type))}
        strategy={verticalListSortingStrategy}
      >
        <div>{list}</div>
      </SortableContext>
    </DndContext>
  );
};

export default SortableActions;
